<template>
	<div v-if="show && steps">
		<ja-modal
			ref="modal"
			class="scrollable-modal modal-component"
			:show="show"
			@close-modal="closeModal"
		>
			<content class="content">
				<perfect-scrollbar ref="scrollBar" class="scrollbar" scroll="true">
					<img
						class="page-icon"
						alt=""
						:src="
							require(`@/assets/images/onboarding-tutorial/Onboarding/onboarding-step-${
								steps[currentStep].imageId
							}${
								steps[currentStep].imageId === 7 && JA_LOCALIZATION_IRL
									? '-ir'
									: ''
							}.png`)
						"
					/>

					<div class="modal-wrapper-content">
						<h2 v-if="steps[currentStep].title" class="heading-3">
							{{ steps[currentStep].title }}
						</h2>
						<div class="body-text-1">
							{{ steps[currentStep].text }}
							<template v-if="steps[currentStep].note" class="note">
								<br />
								<strong>{{ steps[currentStep].note.bolded }}</strong>
								{{ steps[currentStep].note.message }}
							</template>
						</div>

						<ja-financial-health-info
							v-if="steps[currentStep].title == $t('financialHealth')"
							@scrollToTop="scrollToTop"
						></ja-financial-health-info>
					</div>
				</perfect-scrollbar>

				<div v-if="steps[currentStep].title" class="button-wrapper">
					<ja-button
						v-if="currentStep > 0"
						class="back transparent"
						name="back"
						:aria-label="$t('goBackGuidedTourStep')"
						@click.native="goBackStep"
					>
						<svgicon class="back-button-arrow" name="arrow-left"></svgicon>
						{{ $t('back') }}
					</ja-button>

					<ja-progress-tracker
						:num-pages="steps.length"
						:current-page="currentStep"
					></ja-progress-tracker>

					<ja-button
						v-if="currentStep < steps.length - 1"
						class="small next"
						name="next"
						:aria-label="$t('goNextGuidedTourStep')"
						@click.native="goNextStep"
					>
						{{ $t('next') }}
						<svgicon name="arrow-right"></svgicon>
					</ja-button>
					<ja-button
						v-else
						class="small next"
						name="next"
						:aria-label="$t('closeGuidedTourStep')"
						@click.native="closeModal"
					>
						{{ $t('close') }}
					</ja-button>
				</div>
				<ja-close-button
					class="close-button"
					name="close"
					@click.native="closeModal"
					@keydown.esc="closeModal"
				></ja-close-button>
			</content>
		</ja-modal>
	</div>
</template>
<script>
import '../icons/modal-close';
import '../icons/calculation';
import '../icons/score';
import '../icons/target';
import '../icons/paying';
import '../icons/general-savings';

import Modal from './Modal';
import Button from '../ui/Button';
import ProgressTracker from '../ui/ProgressTracker';
import LifeStageData from '../../mixins/LifeStageData';
import ProfileData from '../../mixins/ProfileData';
import gql from 'graphql-tag';
import UserData from '../../mixins/UserData';
import FinancialHealthInfo from '../FinancialHealthInfo/FinancialHealthInfo';
import CloseButton from '../ui/CloseButton';
import ModalEvents from '@/mixins/ModalEvents';

export default {
	components: {
		jaModal: Modal,
		jaProgressTracker: ProgressTracker,
		jaButton: Button,
		jaFinancialHealthInfo: FinancialHealthInfo,
		jaCloseButton: CloseButton
	},
	mixins: [UserData, LifeStageData, ProfileData, ModalEvents],
	props: {
		show: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			currentStep: 0,
			JA_LOCALIZATION_IRL: this.$store.getters.isIrl
		};
	},
	computed: {
		steps() {
			if (this.profileData) {
				let steps = [
					{
						title: this.$t('welcomeToJAFinancePark'),
						text: this.$t('welcomeToJAFinanceParkText')
					},
					{
						id: 'calendar',
						title: this.$t('calendar'),
						text: this.profileData.isEntryVirtualSimulationType
							? this.$t('calendarTextEntry')
							: this.$t('calendarText')
					},
					{
						title: this.$t('research'),
						text: this.$t('researchText')
					},
					{
						title: this.$t('confirmBudgetTitle'),
						text: this.$t('confirmBudgetText')
					},
					{
						title: this.$t('shopAndBuy'),
						text: this.$t('shopAndBuyText')
					},
					{
						title: this.$t('debtPayments'),
						text: this.profileData.isEntryVirtualSimulationType
							? this.$t('debtPaymentsTextEntry')
							: this.$t('debtPaymentsText')
					},
					{
						title: this.$t('stayOnBudget'),
						text: this.$t('stayOnBudgetText'),
						note: {
							bolded: this.$t('stayOnBudgetNoteBolded'),
							message: this.profileData.isEntryVirtualSimulationType
								? this.$t('stayOnBudgetNoteMessageEntry')
								: this.$t('stayOnBudgetNoteMessage')
						}
					}
				];

				if (!this.profileData.isEntryVirtualSimulationType) {
					steps.push({
						title: this.$t('financialHealth'),
						text: this.$t('financialHealthText')
					});
				}

				steps.push({
					title: this.$t('goodLuck'),
					text: this.$t('goodLuckText')
				});

				steps.forEach((item, index) => {
					item.imageId = index + 1;
				});

				if (this.$store.getters.isSimulationType('onsite')) {
					steps = steps.filter(step => step.id !== 'calendar');
				}

				return steps;
			}
			return null;
		}
	},
	methods: {
		scrollToTop() {
			this.$refs.scrollBar.$el.scrollTop = 0;
		},
		closeModal() {
			if (!this.userData.hideOnboardingTutorialPopup) {
				this.hideModalIntro();
			}
			this.$store.commit('toggleOnboarding', false);
			this.currentStep = 0;
		},
		goBackStep() {
			if (this.currentStep < this.steps.length + 1) {
				this.currentStep--;
			}
			this.announceStep();
		},
		goNextStep() {
			if (this.currentStep < this.steps.length - 1) {
				this.currentStep++;
			}
			this.announceStep();
		},
		announceStep() {
			const step = this.steps[this.currentStep];
			const textToAnnounce = [];
			if (step.title) {
				textToAnnounce.push(`${step.title};`);
			}

			if (step.text) {
				textToAnnounce.push(step.text);
			}

			if (step.note) {
				textToAnnounce.push(`${step.note.bolded}${step.note.message}`);
			}
			this.$announcer.polite(textToAnnounce.join(''));
		},
		async hideModalIntro() {
			const modalStatusInfo = {
				id: this.userData.studentId,
				hideOnboardingTutorialPopup: true
			};

			const response = await this.$apollo.mutate({
				mutation: gql`
					mutation($update: StudentInputObjectGraphType!) {
						updateIgnoreNullValuesStudent(update: $update) {
							id
						}
					}
				`,
				variables: {
					update: modalStatusInfo
				}
			});
			if (response.data) {
				this.modalIsEnabledFromBE = false;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.modal-component {
	z-index: 102;
	::v-deep .modal {
		bottom: 0;
		min-height: 300px;
		margin: auto;
		max-width: 500px;
		max-height: 700px;
		@media (min-width: $breakpoint-mid) {
			max-width: 600px;
		}

		.ps {
			height: 100%;
		}
	}
}

content {
	position: relative;
	align-items: inherit !important;
	height: 100%;
	padding-top: 24px;
	::-webkit-scrollbar {
		width: 0px;
	}

	.ps {
		overflow: auto !important;
		overflow-x: auto !important;
		overflow-y: auto !important;
		overflow-anchor: none;
		-ms-overflow-style: none;
		touch-action: auto;
		-ms-touch-action: auto;
		scrollbar-width: none;
	}

	.page-icon {
		max-width: 330px;
		max-height: 250px;
		align-self: center;
	}

	.close-button {
		z-index: 1;
	}

	.guided-tour {
		text-transform: uppercase;
		font-weight: 300;
	}

	h3 {
		margin: 12px 0 12px 0;
	}

	.modal-wrapper-content {
		width: 75%;
		align-self: center;
		margin: auto;
		margin-bottom: 24px;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		align-items: initial;
		flex-grow: 1;

		.note {
			margin-top: 16px;
		}
		.bolded {
			color: $font-header;
			font-weight: 600;
		}
	}
	.progress-tracker {
		position: absolute;
		bottom: 37px;
		width: 100%;
		display: flex;
		justify-content: center;
		right: 0%;
	}

	.button-wrapper {
		flex-shrink: 0;
		height: 90px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-top: 1px solid $container-border;
		position: relative;

		button {
			z-index: 2;
			position: absolute;
			bottom: 50%;
			transform: translate(0, 50%);

			.back-button-arrow {
				width: 11px;
				height: 11px;
				margin-bottom: 2px;
				@media (min-width: $breakpoint-mid) {
					width: 12px;
					height: 12px;
					margin-top: 1px;
				}
			}
		}
		.back {
			left: 32px;
		}
		.next {
			right: 32px;
		}
	}
}
</style>
